import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Loader2, Search, AlertCircle, Check } from "lucide-react";
import { api } from "../lib/api";
import LocalCombobox from "./forms/LocalCombobox";

/**
 * SchoolSelector — cascading county → UAT → school picker.
 *
 * Backed by the `schools` MongoDB collection (16k+ rows imported from the
 * MoE catalogue). The UI fetches:
 *   - GET /api/schools/counties        (once, on mount)
 *   - GET /api/schools/uats?county=X   (when county changes)
 *   - GET /api/schools/search?...      (async, debounced, ≤20 results)
 *
 * Single source of truth lives in the PARENT. This component is
 * fully controlled — it never owns the value, only the open/loading
 * UI state. Call signature:
 *
 *   <SchoolSelector
 *     value={{ county, uat_name, school_name, school_code,
 *              city_original, manual_school_entry }}
 *     onChange={(patch) => setForm(...)}
 *     required
 *     compact={false}
 *   />
 *
 * `onChange` receives a PARTIAL patch — only the fields that changed in
 * this interaction. The parent merges it into its form state.
 *
 * Backward compatibility: if `value.county`/`value.uat_name` come from old
 * data (free-text strings that don't exist in the directory), the dropdowns
 * still display them. Switching to "manual_school_entry: true" hides the
 * dropdowns entirely so admins can correct legacy free-text data.
 */
const FIELDS_BASE =
  "w-full rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 text-sm text-[#373A45] " +
  "outline-none focus:bg-white focus:border-[#965EA7] transition-colors";

function Field({ label, required, optional, children, testid }) {
  return (
    <div className="block" data-testid={testid}>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1.5">
        {label}{required && <span className="text-[#965EA7]"> *</span>}
        {optional && <span className="ml-1.5 text-[10px] font-normal normal-case tracking-normal text-[#9A98A6]">opțional</span>}
      </label>
      {children}
    </div>
  );
}

function useDebounced(value, delayMs = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

export default function SchoolSelector({
  value,
  onChange,
  required = true,
  // County and locality carry the geography the CRM and analytics group on, so
  // they stay tied to `required`. The school itself is the slow field to fill —
  // a searchable directory lookup — and a caller may want it optional on its
  // own. Defaults to `required`, so every existing caller behaves as before.
  //
  // The "opțional" marker below appears only when the school is singled out
  // (`required && !schoolRequired`). Where the whole block is optional — the
  // admin form passes `required={false}` — labelling one of three optional
  // fields would read as if the other two were mandatory.
  schoolRequired = required,
  compact = false,
  fieldClassName = "",
  // A cohort made for one school (12.10.2026): the school is known, shown
  // filled in and locked; county and locality are still asked.
  lockedSchool = "",
}) {
  const v = value || {};
  const [counties, setCounties] = useState([]);
  const [uats, setUats] = useState([]);
  const [loadingUats, setLoadingUats] = useState(false);

  const [query, setQuery] = useState(v.school_name || "");
  const debouncedQuery = useDebounced(query, 280);
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const cls = (fieldClassName || FIELDS_BASE);

  // ---- Bootstrap: counties ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/schools/counties");
        if (alive) setCounties(r.data || []);
      } catch {
        if (alive) setCounties([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ---- County change → fetch UATs ----
  useEffect(() => {
    if (!v.county) { setUats([]); return undefined; }
    let alive = true;
    setLoadingUats(true);
    (async () => {
      try {
        const r = await api.get("/schools/uats", { params: { county: v.county } });
        if (alive) setUats(r.data || []);
      } catch {
        if (alive) setUats([]);
      } finally {
        if (alive) setLoadingUats(false);
      }
    })();
    return () => { alive = false; };
  }, [v.county]);

  // ---- Async school search ----
  useEffect(() => {
    if (v.manual_school_entry) return undefined;
    if (!v.county || !v.uat_name) { setSearchResults([]); return undefined; }
    if (!dropdownOpen) return undefined;
    let alive = true;
    setLoadingSearch(true);
    (async () => {
      try {
        const r = await api.get("/schools/search", {
          params: {
            county: v.county,
            uat: v.uat_name,
            q: debouncedQuery || undefined,
            // Bump to 500 so single-UAT big cities (e.g. București with 437
            // schools under one canonical UAT) return their full list in
            // one shot instead of getting capped at 20.
            limit: 500,
          },
        });
        if (alive) setSearchResults(r.data || []);
      } catch {
        if (alive) setSearchResults([]);
      } finally {
        if (alive) setLoadingSearch(false);
      }
    })();
    return () => { alive = false; };
  }, [v.county, v.uat_name, debouncedQuery, dropdownOpen, v.manual_school_entry]);

  // ---- Close dropdown when clicking outside ----
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // ---- Handlers (school + manual fallback only; county/uat are inlined into LocalCombobox) ----
  const onPickSchool = useCallback((school) => {
    onChange({
      school_name: school.school_name,
      school_code: school.school_code,
      city_original: school.city_original || "",
      manual_school_entry: false,
    });
    setQuery(school.school_name);
    setDropdownOpen(false);
  }, [onChange]);

  const onToggleManual = useCallback((e) => {
    const checked = e.target.checked;
    if (checked) {
      onChange({
        school_name: "",
        school_code: null,
        city_original: "",
        manual_school_entry: true,
      });
      setQuery("");
    } else {
      onChange({
        school_name: "",
        school_code: null,
        city_original: "",
        manual_school_entry: false,
      });
    }
  }, [onChange]);

  const onManualSchoolName = useCallback((e) => {
    onChange({
      school_name: e.target.value,
      school_code: null,
      manual_school_entry: true,
    });
  }, [onChange]);

  // ---- Render ----
  const grid = compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 md:grid-cols-2 gap-4";

  const schoolDisabled = !v.county || !v.uat_name;
  // If the saved county isn't in the loaded list yet (or is legacy free-text),
  // still show it as an option so the form value persists across renders.
  const countyOptions = useMemo(() => {
    if (!v.county || counties.includes(v.county)) return counties;
    return [...counties, v.county];
  }, [counties, v.county]);
  const uatOptions = useMemo(() => {
    if (!v.uat_name || uats.includes(v.uat_name)) return uats;
    return [...uats, v.uat_name];
  }, [uats, v.uat_name]);

  return (
    <div ref={containerRef} className="space-y-4" data-testid="school-selector">
      <div className={grid}>
        <Field label="Județ" required={required} testid="school-selector-county-field">
          <LocalCombobox
            value={v.county || ""}
            onChange={(val) => onChange({
              county: val,
              uat_name: "",
              school_name: "",
              school_code: null,
              city_original: "",
              manual_school_entry: false,
            })}
            options={countyOptions}
            placeholder="Selectează județul"
            required={required}
            testid="school-selector-county"
          />
        </Field>

        <Field label="Localitate (UAT)" required={required} testid="school-selector-uat-field">
          <LocalCombobox
            value={v.uat_name || ""}
            onChange={(val) => onChange({
              uat_name: val,
              school_name: "",
              school_code: null,
              city_original: "",
              manual_school_entry: false,
            })}
            options={uatOptions}
            placeholder={loadingUats ? "Se încarcă…" : "Selectează localitatea"}
            fallbackPlaceholder="Alege întâi județul"
            disabled={!v.county}
            required={required}
            testid="school-selector-uat"
          />
        </Field>
      </div>

      {/* School: combobox (or manual input when fallback is on) */}
      {lockedSchool ? (
        <Field label="Unitate de învățământ" testid="school-selector-school-field">
          <input type="text" value={lockedSchool} readOnly aria-readonly="true" tabIndex={-1}
            className={`${FIELDS_BASE} cursor-not-allowed text-[#6B7280]`}
            data-testid="school-selector-locked" />
          <p className="mt-1.5 text-xs text-[#6B7280]">Grupă dedicată acestei unități: câmpul e completat de FPED.</p>
        </Field>
      ) : (
      <>
      <Field label="Unitate de învățământ" required={schoolRequired} optional={required && !schoolRequired} testid="school-selector-school-field">
        {!v.manual_school_entry ? (
          <div className="relative">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
              <input
                type="text"
                className={cls + " pl-10 clarity-mask"}
                data-clarity-mask="true"
                placeholder={schoolDisabled
                  ? "Alege întâi județul și localitatea"
                  : "Caută unitatea (ex. Eminescu, Liceu, Școala 1…)"}
                value={query}
                disabled={schoolDisabled}
                onFocus={() => !schoolDisabled && setDropdownOpen(true)}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setDropdownOpen(true);
                  // user is typing — clear any previously locked-in school
                  if (v.school_code) {
                    onChange({ school_name: e.target.value, school_code: null, city_original: "" });
                  } else {
                    onChange({ school_name: e.target.value });
                  }
                }}
                required={schoolRequired && !v.manual_school_entry}
                data-testid="school-selector-school-input"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              {loadingSearch && (
                <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#965EA7]" />
              )}
              {!loadingSearch && v.school_code && (
                <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1F7A3D]" />
              )}
            </div>

            {dropdownOpen && !schoolDisabled && (
              <div
                className="absolute z-30 mt-1 w-full rounded-lg bg-white shadow-lg border border-[#E5E7EB] max-h-72 overflow-auto"
                data-testid="school-selector-results"
              >
                {searchResults.length === 0 && !loadingSearch && (
                  <div className="px-4 py-3 text-sm text-[#6B7280]" data-testid="school-selector-empty">
                    {debouncedQuery
                      ? "Nicio unitate găsită pentru această căutare."
                      : "Începe să tastezi numele unității…"}
                  </div>
                )}
                {searchResults.map((s) => (
                  <button
                    key={s.school_code}
                    type="button"
                    onClick={() => onPickSchool(s)}
                    className="w-full text-left px-4 py-2 hover:bg-[#F5F6F6] focus:bg-[#EDEEF0] focus:outline-none"
                    data-testid={`school-selector-option-${s.school_code}`}
                  >
                    <div className="text-sm text-[#373A45] font-medium">{s.school_name}</div>
                    <div className="text-xs text-[#6B7280]">
                      {s.city_original}{s.city_original && s.uat_name ? " · " : ""}{s.uat_name}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <input
            type="text"
            className={cls + " clarity-mask"}
            data-clarity-mask="true"
            placeholder="Introdu manual denumirea unității"
            value={v.school_name || ""}
            onChange={onManualSchoolName}
            required={schoolRequired}
            data-testid="school-selector-manual-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        )}
      </Field>

      <label className="flex items-start gap-2 text-xs text-[#6B7280]" data-testid="school-selector-manual-toggle-label">
        <input
          type="checkbox"
          className="mt-0.5 h-3.5 w-3.5 accent-[#965EA7]"
          checked={!!v.manual_school_entry}
          onChange={onToggleManual}
          data-testid="school-selector-manual-toggle"
        />
        <span className="leading-snug">
          <AlertCircle size={12} className="inline -mt-0.5 mr-1" />
          Nu găsesc unitatea de învățământ — completez manual
        </span>
      </label>
      </>
      )}
    </div>
  );
}
